import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Translations from '@helpers/translations'
import { ReturnsHeaderImage } from '@components/returns/ReturnsHeaderImage'
import Notification from '@components/ui/Notification'
import { getReturnStatus } from '@api/returns'
import ItemsSelection, { ItemSelectionOnSubmitProps } from './ItemsSelection'
import CarrierSelection from './CarrierSelection'
import ReturnsHeader from './ReturnsHeader'
import OrderForm from './OrderForm'
import styles from './styles.module.scss'
import formData from './formData'
import Footer from '@components/Footer'

type SelectedOrder = Omit<ItemSelectionOnSubmitProps, 'queryParams'>

function toQueryString(
  params: Record<string, string | number | boolean | null | undefined>
): string {
  const cleanedEntries = Object.entries(params)
    .filter(([, value]) => value != null) // drop null/undefined
    .map(([key, value]) => [key, String(value)] as [string, string])

  return new URLSearchParams(cleanedEntries).toString()
}

const ReturnPageContent = () => {
  const router = useRouter()
  const {
    order_number: orderNumber,
    postcode,
    successReturn,
    admin
  } = router.query as {
    order_number: string | undefined
    postcode: string | undefined
    successReturn: string | undefined
    admin: string | undefined
  }
  const [isClient, setIsClient] = useState(false)
  const [order, setOrder] = useState<SelectedOrder>()

  useEffect(() => {
    setIsClient(true)
  }, [])

  const onSubmit = ({
    salesOrderId,
    customerAddress,
    productsToReturn,
    carriers,
    queryParams
  }: ItemSelectionOnSubmitProps) => {
    setOrder({ productsToReturn, carriers, salesOrderId, customerAddress })
    const { query } = router

    const mergedQuery = {
      ...query,
      ...queryParams,
      pagesHandle: undefined,
      pageHandle: undefined,
      error: undefined
    }

    const href = `/pages/returns?${toQueryString(mergedQuery)}`

    router.push(href, undefined, { shallow: true })
  }

  if (process.env.RETURNS_MAINTENANCE_MODE === 'true' && admin !== 'true') {
    return (
      <>
        <ReturnsHeaderImage content={formData.header} />
        <Notification
          type="warning"
          content={
            <>
              <p>
                {Translations.localise(
                  'pages.returns.returns-notifications.maintenance-mode.copy-1'
                )}
              </p>
              <p>
                {Translations.localise(
                  'pages.returns.returns-notifications.maintenance-mode.copy-2'
                )}
              </p>
            </>
          }
        />
      </>
    )
  }

  if (orderNumber && postcode && order?.carriers && order?.customerAddress) {
    return (
      <CarrierSelection
        headerImage={formData.header.image}
        orderNumber={orderNumber}
        postcode={postcode}
        carriers={order.carriers}
        productsToReturn={order.productsToReturn}
        salesOrderId={order.salesOrderId}
        customerAddress={order.customerAddress}
      />
    )
  }

  if (orderNumber && postcode) {
    return (
      <ItemsSelection
        successReturn={successReturn}
        orderNumber={orderNumber}
        postcode={postcode}
        onSubmit={onSubmit}
      />
    )
  }

  return isClient && <OrderForm formData={formData} onSubmit={onSubmit} />
}

const ReturnPage = () => {
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false)

  useEffect(() => {
    getReturnStatus()
      .then(response => {
        setIsServiceUnavailable(response.status !== 200)
      })
      .catch(() => {
        setIsServiceUnavailable(true)
      })
  }, [])

  return (
    <div className={styles.returns__wrapper}>
      <ReturnsHeader />

      {isServiceUnavailable && (
        <Notification
          type="error"
          theme="dark"
          content={
            <p>
              We are currently experiencing some issues with our returns
              provider. <br />
              If your order cannot be found, please try again in 24 hours. If
              it’s urgent, email us at hello@spoke-london.com.
              <br />
              We apologise for any inconvenience.
            </p>
          }
        />
      )}
      <div className={styles.returns__contentWrapper}>
        <ReturnPageContent />
      </div>
      <Footer />
    </div>
  )
}

export default ReturnPage
